import { ReactNode, useContext } from 'react';
import { useIntl } from 'react-intl';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { AttributeCode, MetricCode, useModelService } from 'services/model/model.service';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { TradingMessage } from 'pages/trading/model';
import { TranslatedAttributeLabel } from 'pages/trading/TranslatedAttributeLabel';
import { Tooltip } from 'components/Tooltip';

// Regex patterns for parsing message tags
const ATTR_REGEX = /\[\s*([^\]:]+)::\s*([^\]]+)\s*]/;
const DATE_REGEX = /\[\s*D-([^\]]+)\s*]/;
const CURRENCY_REGEX = /\[\s*C-([^\]]+)\s*]/;
const MEASURE_REGEX = /\[\s*M-([^\]]+)\s*]/;
const PERCENT_REGEX = /\[\s*P-([^\]]+)\s*]/;
const STYLE_TAG_REGEX = /\[\s*S-([^|]+)\|\s*([^\]]+)\s*]/;

export interface MessageTagParser {
  translateText: (text: string, message: TradingMessage) => ReactNode[];
}

export function useMessageTagParser(): MessageTagParser {
  const { getMeasureByMetric } = useModelService();
  const intl = useIntl();
  const { currencyCode } = useContext(TenantConfigContext);

  const formatNumberByUnitCode = (numberText: string, unitCode: MeasureUnitCode): string => {
    if (numberText === undefined || numberText === null) {
      return '';
    }
    const cleanNumber = numberText.replace('%', '');
    const val = parseFloat(cleanNumber);

    if (isNaN(val)) return numberText;

    switch (unitCode) {
      case MeasureUnitCode.CURRENCY:
        return intl.formatNumber(val, {
          style: 'currency',
          currency: currencyCode,
        });
      case MeasureUnitCode.PERCENT:
        return `${intl.formatNumber(val, { maximumFractionDigits: 2 })}%`;
      default:
        return numberText;
    }
  };

  const replaceDate = (_: string, datestring: string) => {
    const date = new Date(datestring);
    return isNaN(date.getTime())
      ? datestring
      : intl.formatDate(date, { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'long' });
  };

  const replaceCurrency = (_: string, currencyString: string) => {
    const val = currencyString.split('||')[0];
    return intl.formatNumber(parseFloat(val), {
      style: 'currency',
      currency: currencyCode,
    });
  };

  const replaceMeasureCode = (segment: string): ReactNode => {
    const renderRawSegment = (): ReactNode => <span>{segment}</span>;
    try {
      const match = segment.match(MEASURE_REGEX);
      if (match && match.length > 1) {
        const matchedString = match[1];
        const measureWithPotentialNumber = matchedString.split('||');
        if (measureWithPotentialNumber.length > 1) {
          const currencyString = measureWithPotentialNumber[0];
          const metricCode = measureWithPotentialNumber[1] as MetricCode;
          let tooltip: string;
          try {
            const measure = getMeasureByMetric(metricCode, 'OD');
            tooltip = measure ? measure.metric.name : '';
          } catch (e) {
            console.warn(`Metric code not found for tooltip: ${metricCode}`, e);
            tooltip = metricCode;
          }

          return (
            <Tooltip tooltipContent={tooltip} inline>
              <span className="font-semibold">{formatNumberByUnitCode(currencyString, MeasureUnitCode.CURRENCY)}</span>
            </Tooltip>
          );
        }

        const measure = getMeasureByMetric(measureWithPotentialNumber[0] as MetricCode, 'OD');
        return <span>{measure?.metric.name ?? measureWithPotentialNumber[0]}</span>;
      }
    } catch (error) {
      console.warn('Error rendering measure tag, falling back to raw value:', segment, error);
      return renderRawSegment();
    }
    return renderRawSegment();
  };

  const replacePercentCode = (segment: string): ReactNode => {
    const match = segment.match(PERCENT_REGEX);
    if (match && match.length > 1) {
      const matchedString = match[1];
      const parts = matchedString.split('||');
      const valString = parts[0];

      return (
        <span className="font-semibold text-gray-900">
          {formatNumberByUnitCode(valString, MeasureUnitCode.PERCENT)}
        </span>
      );
    }
    return <span>{segment}</span>;
  };

  const renderStyle = (segment: string): ReactNode => {
    const match = segment.match(STYLE_TAG_REGEX);
    if (match) {
      const [, id, name] = match;
      return (
        <span>
          <span className="font-semibold">{name.trim()}</span> ({id.trim()})
        </span>
      );
    }
    return <span>{segment}</span>;
  };

  const createSegmentNode = (segment: string, msg: TradingMessage): ReactNode => {
    if (ATTR_REGEX.test(segment)) {
      const match = segment.match(ATTR_REGEX);
      if (match) {
        const [, key, value] = match;
        return (
          <span className={msg.keyAttributes?.includes(key as AttributeCode) ? 'font-semibold' : undefined}>
            <TranslatedAttributeLabel attributeCode={key} attributeValue={value} />
          </span>
        );
      }
    }
    if (DATE_REGEX.test(segment)) {
      return <span>{segment.replace(DATE_REGEX, replaceDate)}</span>;
    }
    if (CURRENCY_REGEX.test(segment)) {
      return <span>{segment.replace(CURRENCY_REGEX, replaceCurrency)}</span>;
    }
    if (MEASURE_REGEX.test(segment)) {
      return replaceMeasureCode(segment);
    }
    if (PERCENT_REGEX.test(segment)) {
      return replacePercentCode(segment);
    }
    if (STYLE_TAG_REGEX.test(segment)) {
      return renderStyle(segment);
    }

    return <span>{segment}</span>;
  };

  const translateText = (text: string, message: TradingMessage): ReactNode[] => {
    const splitRegex = /(\[[^\]]+])/g;
    return text
      .split(splitRegex)
      .filter(Boolean)
      .map((s, i) => <span key={i}>{createSegmentNode(s, message)}</span>);
  };

  return {
    translateText,
  };
}
